/**
 * Route Loaders
 * =============
 * Single source of truth for code-split pages. Home stays a regular
 * (non-lazy) import in App.js since it's the most common entry point and
 * shouldn't pay a dynamic-import round trip; every other page is lazy so
 * a visitor never downloads code for pages they haven't navigated to.
 *
 * Each loader is also exported standalone (not just wrapped in `lazy()`)
 * so links can call the exact same dynamic import on hover/focus, ahead
 * of an actual click - the browser caches the chunk, so by the time
 * React.lazy's Suspense boundary needs it, it has often already resolved
 * and the fallback never has to show. See Button.jsx / Navigation.jsx.
 */

import { lazy } from 'react';

export const routeLoaders = {
  about: () => import('./About'),
  menu: () => import('./Menu'),
  ordering: () => import('./Ordering'),
  reservations: () => import('./Reservations'),
  rewards: () => import('./Rewards'),
  location: () => import('./Location'),
  contact: () => import('./Contact'),
  privacyPolicy: () => import('./PrivacyPolicy'),
  terms: () => import('./Terms')
};

export const About = lazy(routeLoaders.about);
export const Menu = lazy(routeLoaders.menu);
export const Ordering = lazy(routeLoaders.ordering);
export const Reservations = lazy(routeLoaders.reservations);
export const Rewards = lazy(routeLoaders.rewards);
export const Location = lazy(routeLoaders.location);
export const Contact = lazy(routeLoaders.contact);
export const PrivacyPolicy = lazy(routeLoaders.privacyPolicy);
export const Terms = lazy(routeLoaders.terms);

// Route path -> loader, so any internal link can prefetch its target
// route's chunk generically without knowing which page component it maps
// to. Intentionally omits "/" (Home isn't code-split) and the legacy
// redirect paths (they resolve to one of these same targets anyway).
export const PATH_TO_LOADER = {
  '/about-us': routeLoaders.about,
  '/menu': routeLoaders.menu,
  '/order-online': routeLoaders.ordering,
  '/book-table': routeLoaders.reservations,
  '/rewards': routeLoaders.rewards,
  '/location': routeLoaders.location,
  '/contact-us': routeLoaders.contact,
  '/privacy-policy': routeLoaders.privacyPolicy,
  '/terms': routeLoaders.terms
};

// The pages most visitors actually go looking for - warmed proactively
// shortly after the app goes idle, since touch devices have no hover
// state to trigger the on-demand prefetch below.
export const PRIORITY_PREFETCH_PATHS = ['/menu', '/order-online', '/book-table', '/rewards', '/location'];

export const prefetchRoute = (path) => {
  if (!path) return;
  const loader = PATH_TO_LOADER[path.split('#')[0].split('?')[0]];
  if (loader) loader();
};
